<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { useSuccessStore } from '@/store/success'
import { toMonthKey } from '@/utils/date/date-time'
import { IManualInvoiceParams, ManualInvoiceMetricKey } from '@/interfaces/api/energyInvoice'
import MonthYearPicker from '@/components/Globals/Inputs/MonthYearPicker.vue'
import IcLoader from '@/components/Icons/IcLoader.vue'

const props = defineProps<{ isOpen: boolean }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { t } = useI18n()
const store = useFareOptimizerStore()
const { requiresConsumerUnitSelection, selectedConsumerUnit, isSavingInvoice } = storeToRefs(store)

// Campos do formulário, com rótulo e unidade. Espelha as colunas promovidas.
const FIELDS: { key: ManualInvoiceMetricKey; label: string; unit: string }[] = [
  { key: 'consumo_ponta', label: 'Consumo Ponta', unit: 'kWh' },
  { key: 'consumo_fora_ponta', label: 'Consumo Fora Ponta', unit: 'kWh' },
  { key: 'consumo_intermediario', label: 'Consumo Intermediário', unit: 'kWh' },
  { key: 'consumo_total', label: 'Consumo Total', unit: 'kWh' },
  { key: 'demanda_ponta_medida', label: 'Demanda Ponta', unit: 'kW' },
  { key: 'demanda_fora_ponta_medida', label: 'Demanda Fora Ponta', unit: 'kW' },
  { key: 'demanda_ponta_contratada', label: 'Demanda Contr. Ponta', unit: 'kW' },
  { key: 'demanda_fora_ponta_contratada', label: 'Demanda Contr. Fora Ponta', unit: 'kW' },
  { key: 'reativo_ponta', label: 'Reativo Ponta', unit: 'kVArh' },
  { key: 'reativo_fora_ponta', label: 'Reativo Fora Ponta', unit: 'kVArh' },
  { key: 'custo_total', label: 'Custo Total', unit: 'R$' },
]

const referenceMonth = ref('')
const distribuidora = ref('')
const values = reactive<Record<string, string>>({})

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    // Default: mês anterior; limpa o formulário.
    const now = new Date()
    referenceMonth.value = toMonthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1))
    distribuidora.value = ''
    FIELDS.forEach((field) => (values[field.key] = ''))
    store.fetchInvoices()
  },
)

// Fatura já existente para o mês escolhido (alerta de sobrescrita).
const existing = computed(() => store.savedInvoices.find((invoice) => invoice.reference_month?.slice(0, 7) === referenceMonth.value))

const filledMetrics = computed(() => {
  const out: IManualInvoiceParams['metrics'] = {}
  FIELDS.forEach((field) => {
    const raw = values[field.key]?.toString().trim()
    if (raw) out[field.key] = Number(raw.replace(/\./g, '').replace(',', '.'))
  })
  return out
})

const canSave = computed(
  () =>
    !!referenceMonth.value &&
    Object.keys(filledMetrics.value).length > 0 &&
    !requiresConsumerUnitSelection.value,
)

async function confirm() {
  if (!canSave.value) return
  const ok = await store.saveManualInvoice({
    reference_month: referenceMonth.value,
    metrics: filledMetrics.value,
    distribuidora: distribuidora.value || undefined,
  })
  if (!ok) return
  useSuccessStore().setSuccess([t('contract_optimizer.invoice.saved_success')])
  emit('close')
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/40" @click="emit('close')" />
    <div class="relative flex max-h-[90vh] w-full max-w-2xl flex-col bg-white p-6 shadow-xl">
      <h3 class="text-lg font-semibold text-slate-900">{{ t('contract_optimizer.invoice_history.manual_title') }}</h3>
      <p class="mt-1 text-sm text-slate-500">{{ t('contract_optimizer.invoice_history.manual_hint') }}</p>

      <p v-if="selectedConsumerUnit" class="mt-3 text-sm text-slate-600">
        <span class="font-medium text-black">{{ selectedConsumerUnit.name }}</span>
        <span class="ml-2 text-slate-400">{{ selectedConsumerUnit.code }}</span>
      </p>
      <p v-else-if="requiresConsumerUnitSelection" class="mt-3 text-sm text-red">
        {{ t('contract_optimizer.consumer_units.select_required') }}
      </p>

      <div class="mt-4 space-y-4 overflow-y-auto">
        <div class="flex flex-col gap-2 sm:flex-row">
          <div class="flex flex-1 flex-col gap-2">
            <label class="text-sm font-medium text-slate-700">{{ t('contract_optimizer.invoice.reference_month') }}</label>
            <MonthYearPicker v-model="referenceMonth" />
          </div>
          <div class="flex flex-1 flex-col gap-2">
            <label class="text-sm font-medium text-slate-700">{{ t('contract_optimizer.invoice.distribuidora') }}</label>
            <input v-model="distribuidora" class="h-11 w-full border-2 border-stroke px-4 focus:border-blue focus:outline-none" />
          </div>
        </div>

        <p v-if="existing?.complete" class="bg-amber-100 px-4 py-3 text-sm text-amber-800">
          {{ t('contract_optimizer.invoice.overwrite_warning') }}
        </p>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div v-for="field in FIELDS" :key="field.key" class="flex flex-col gap-1">
            <label class="text-xs font-medium uppercase text-slate-500">{{ field.label }} ({{ field.unit }})</label>
            <input
              v-model="values[field.key]"
              type="text"
              inputmode="decimal"
              placeholder="—"
              class="h-10 w-full border-2 border-stroke px-3 text-right focus:border-blue focus:outline-none" />
          </div>
        </div>
      </div>

      <div class="mt-4 flex justify-end gap-3 border-t border-stroke pt-4">
        <button type="button" class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" @click="emit('close')">
          {{ t('contract_optimizer.invoice_history.cancel') }}
        </button>
        <button
          type="button"
          :disabled="!canSave || isSavingInvoice"
          class="inline-flex items-center gap-2 bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          @click="confirm">
          <IcLoader v-if="isSavingInvoice" class="h-4 w-4 animate-spin" />
          {{ t('contract_optimizer.invoice_history.manual_save') }}
        </button>
      </div>
    </div>
  </div>
</template>
